import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { Observable, OperatorFunction, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { EditableFieldBase } from '../editable-field-base';

export enum InputMode {
  Text,             
  TextArea,        
  Number,
}

export enum FormatSpecifier {
  None,
  CostCode, 
  DollarsAndCents,
  DollarsOnly,
  NumberFixed0,
  NumberFixed2,
  NumberFixed3,
  Digits, // Not a number amount, e.g epar, pid or request id

}

// TYPEAHEAD SUPPORT INSTRUCTIONS 
// For TypeAhead support, include an observable to an array of objects that has the following properties.
// The objects may contain any other properties the application might require. The value of the control
// will contain the entire object, or just a string if no object is selected. 
//    * name (required) -- the display name to show in the pop-up and textbox when selected
//    * desc (optional) -- short descriptive text to display under the name
//    * img (optional) -- an image to display at the left of the option 

@Component({
    selector: 'text-input',
    templateUrl: './text-input.component.html',
    styleUrls: ['./text-input.component.scss', '../editable-fields.scss'],
    standalone: false
})
export class TextInput extends EditableFieldBase implements OnInit, OnChanges {

  @Input() mode: InputMode = InputMode.Text;                   // mode - default is 'text' other values can be: 'textarea', 'number'
  @Input() maxLength: number;                     // the maximum character length of the input field
  @Input() typeaheadList$: (query: string) => Observable<{ name }[]>;  // a callback method used to fetch the typeahead list
  @Input() placeholder: string = '';              // what to show when the value is empty
  @Input() displayFormat: FormatSpecifier = FormatSpecifier.None;       //  pass in a desired display format for
  @Input() requireTypeaheadSelection: boolean = false;                  //  For use in the typeahead input when the value MUST be selected from the list of options.
  @Input() textAreaDefaultHeight: number = 0;
  @Input() showInputErrorIcon: boolean = false;                          // Optional feature to show an error icon inside the input
  @Input() showInputWarningIcon: boolean = false;                        // Optional feature to show a warning icon inside the input

  //  This is an override used for when we want to prevent data changes while lookups are occuring.  It should be used only temporarily, not to permanently
  //  disable an input in the UI.  Disabling the input triggers a spinner icon in the UI to the right of the input.
  @Input() isDisabled: boolean = false;

  // IMPORTANT: If we are using _displayValue instead of obj[prop] for the ngModel then use the updateValue input and point it to the obj[prop]
  // We are watching this for changes in ngOnChanges and it should be used if you are changing the bound object / property value in the ts code for your component.
  @Input() updateValue: string; 

  // We are not using min and max for the number fields, we can handle this in the field validation. But leaving this here in case its useful later
  //@Input() minValue: number;                      // for number fields - the minimum value
  //@Input() maxValue: number;                      // for number fields - the maximum value

  @Input() step: string = 'any';                          // the step amount for number fields.

  //  IMPORTANT:  Only use this for small static lists of options like pronouns, not potentially big api calls like a person lookup.
  @Input() focusOpenTypeahead: boolean = false;          //  Allows the user to see a list of options without having to type anything into the search.

  @Input() typeaheadFilter: string;                 //  Optional for typeaheads.  Allows you to refine the filter further beyond the text input value.

  @Output() optionSelectEmitter$: EventEmitter<any> = new EventEmitter<any>();  //  Emits when a typeahead option has been selected

  //  Both ViewChilds are used with the focusOpenTypeahead
  @ViewChild('ngbInstance') ngbInstance: NgbTypeahead;
  @ViewChild('ngbElement') ngbElement: ElementRef;

  protected _displayValue: string; 
  protected _actualValue: string | number | object; 

  // Add a get for inputMode so we can use it in the html
  public get inputMode(): typeof InputMode {
    return InputMode;
  }

  public get formatSpecifier(): typeof FormatSpecifier {
    return FormatSpecifier;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    // Set the display value to the value provided in the bound object/property, and onEdit will format it
    // and set the actual value accordingly.
       
    if (!!changes.obj) {
      this._displayValue = this.obj[this.prop];
      this.onEdit(this._displayValue);
    }
   
    //  Temporary fix for updateValue not actually updating with the correct format in the UI.
    //  Currently be used for Budget Manager and My HR Requests.
    if (!!changes.updateValue && (changes.updateValue.currentValue != undefined)) {
      
      let val = changes.updateValue.currentValue;
      
      //  If this is a number input or a typeahead we don't want to break the value by turning it
      //  into a string.
      if ((this.mode != InputMode.Number) && (typeof val != 'object'))
        val = val.toString();

      let event = {
        target: {
          value: val
        }
      }

      //  In onInputChange we do a "short circuit" for number inputs.  If we do not set the type
      //  on the event target then if the field is read only and it's updated by another process
      //  or input the UI will not update for this input.
      if (this.mode == InputMode.Number)
        event.target['type'] = 'number';

      this.onInputChange(event);
    }   
  }

  public ngOnInit() {
    super.ngOnInit();
    
    let useFormatNumber = this.displayFormat == this.formatSpecifier.DollarsAndCents
      || this.displayFormat == this.formatSpecifier.DollarsOnly
      || this.displayFormat == this.formatSpecifier.CostCode

    // Warn the developer if they are trying to use a format specifier but are not using text mode
    if (this.mode == InputMode.Number && useFormatNumber) {
      throw new Error(`Invalid configuration: input mode must be text in order to use a display format for ${this.prop}`);
    }
    // Set the display value to the value provided in the bound object/property, and onEdit will format it
    // and set the actual value accordingly.  If this is a number we want to make a display value even if
    // the value is zero.
    if (this.obj[this.prop] || (typeof this.obj[this.prop] === "number")) 
      this._displayValue = this.obj[this.prop];

   
    // We should apply the appropriate formatting for costcodes and dollar amounts when
    // an input inits as readonly
    // This is currently being used in HR Positions, FPS and Budget Manager
   

    if (useFormatNumber)
      this.formatNumber(this._displayValue)
               
    this.onEdit(this._displayValue);
  }

  // Call this when a user makes any change or edit within the html control but has not yet tabbed out or
  // otherwise committed that change. For example, for each keystroke that the user types. This method applies
  // any masking and manages both the displayed and actual value properties.
  //    This method is intended to be idempotent, so call it as often as needed and it shouldn't have any adverse
  // effects. (I say eye-DEM-po-tent, but apparently most people say EYE-dem-PO-tent, which to me just sounds
  // weird.)
  protected onEdit(newValue: any) { // value can be either a string of a typeahead object
    // If the user has accepted a typeahead value, then use that value as the actual.    
    if (!!newValue && (typeof newValue !== 'string')) {
      this.obj[this.prop] = newValue;
      this._actualValue = newValue;
      this._displayValue = newValue.name ?? newValue;
      this.validate(); 
    }
  }

  //  Emits when an option has been selected from a typeahead dropdown.  Useful if you need to trigger an
  //  action when something is selected vs when something is typed into the input.
  protected onTypeaheadOptionSelect(value: any) {
    if (typeof value == 'object') {
      this.onChange();
      this.optionSelectEmitter$.emit(value);
    }
  }

  // Call this when the user has committed to a value change. This could be anything from a single checkbox
  // click or settinging several form values and clicking "ok" or things like that. 
  protected onChange() {
    //  If this is a typeahead that requires a value to be selected from the list, reset the input if the
    //  user exits the input without an option selected
    if (!!this.requireTypeaheadSelection && !this._actualValue['name']) {
      this._actualValue = '';
      this._displayValue = '';
    }

    this.onEdit( this._displayValue ); // clear up actual and display values 
    this.obj[this.prop] = this._actualValue;
    this.validate(); 
    super.onChange(); 
  }

  public openTypeahead(): void {
    this.ngbElement.nativeElement.dispatchEvent(new Event('input'));
    this.ngbElement.nativeElement.focus();
  }

  public search: OperatorFunction<string, readonly { name }[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(500),                  // we'll standardize the debounce time or consistency across apps
      distinctUntilChanged(),             // we only want changes
      switchMap(searchString => {
        if (this.typeaheadList$ != undefined) {
          if (this.focusOpenTypeahead || searchString.length >= 2) {
            let query = searchString;
            //  If additional filters are needed they should be included with the query before we send the
            //  query to the API.
            if (!!this.typeaheadFilter)
              query += `&${this.typeaheadFilter}`;

            return this.typeaheadList$(query)
          }
          else
              return of([])
        }
        else
          return of([])
      }
      ),
    );

  constructor() {
    super();
  }

  // Format the supplied input based on the current displayformat
  protected formatNumber(input: string, caratPos: number = 0): FormattedString {
    //  These can only be applied to inputs that are of InputMode Text.  They cannot be applied to the Number mode because that
    //  input is limited to numbers and the letter "e".
    let formatted: FormattedString;
    switch (this.displayFormat) {
      case FormatSpecifier.DollarsAndCents:
        formatted = new NumberFormatter("$", "", true, 2).convert(input, caratPos)        
        break;
      case FormatSpecifier.DollarsOnly:
        formatted = new NumberFormatter("$", "", true, 0).convert(input, caratPos)

        break;
      case FormatSpecifier.NumberFixed0:
        formatted = new NumberFormatter("", "", true, 0).convert(input, caratPos)
        break;
      case FormatSpecifier.NumberFixed2:
        formatted = new NumberFormatter("", "", true, 2).convert(input, caratPos)
        break;
      case FormatSpecifier.NumberFixed3:
        formatted = new NumberFormatter("", "", true, 3).convert(input, caratPos)
        break;
      case FormatSpecifier.CostCode:
        formatted = new CostCodeFormatter().convert(input, caratPos)
        break;
      case FormatSpecifier.Digits:
        //  Digits are intended as a form of id and we don't necessarily want to return 0 into the UI.  This
        //  catches that edge case and returns the input to empty;
        if (!input?.trim()?.length) {
          this._displayValue = '';
          this._actualValue = '';
          this.obj[this.prop] = '';
          this.validate();
          return formatted;
        }

        formatted = new NumberFormatter("", "", false, 0).convert(input, caratPos)
        break;
      default:
        formatted = new StringFormatter().convert(input, caratPos)
    }

    // HTML wants the values of text inputs of type number to be strongly typed as numbers, even though
    // Angular does not necessarily support this yet. We'll stick with HTML on this one.
    this._displayValue = formatted.displayValue;

    this._actualValue = (this.mode == InputMode.Number)
      ? Number(formatted.actualValue)
      : formatted.actualValue;

    // Set the object value and validate 
    this.obj[this.prop] = this._actualValue;
    this.validate();
    return formatted;
  }

  @HostListener('input', ['$event'])
  protected onInputChange(event) {
    // Short circuit number inputs    
    if (event.target.type == 'number') {
      this._displayValue = event.target.value;
      this._actualValue = parseFloat(event.target.value);      
      return;
    }

    let formatted = this.formatNumber(event.target.value, event.target.selectionStart);
 
    // Reset the start position so we don't end up after the cents field if we added those. Do these in the next
    // loop because the states on the stack might not be consistent.
    if (!!event.target.selectionStart) {
      setTimeout(() => {
        event.target.value = this._displayValue;
        if (event.target.selectionStart != null)
          event.target.selectionStart = formatted.position;
        if (event.target.selectionEnd != null)
          event.target.selectionEnd = formatted.position;
      });
    }
  }

  // Waaay back in the day when I used to edit MIDI event views, my software had this really handy feature.
  // Not entirely sure how practical it is, but I wanted to try to implement it anyway just for fun. 
  @HostListener('keydown.arrowup', ['$event'])
  @HostListener('keydown.arrowdown', ['$event'])
  protected onVerticalArrowDown(event) {

    // Allow character-by-character increment/decrement using vertical arrow keys
    if (event.target.type == "text") {  // only supported in textboxes 
      event.preventDefault();  // don't allow the cursor to go to the front or end of the box

      switch (this.displayFormat) {
        case FormatSpecifier.DollarsAndCents:
        case FormatSpecifier.DollarsOnly:
        case FormatSpecifier.NumberFixed0:
        case FormatSpecifier.NumberFixed2:
        case FormatSpecifier.NumberFixed3:
        case FormatSpecifier.CostCode:
          // get the amount of offset 
          const diff = (event.code == "ArrowUp") ? 1 : -1;
          const idx = event.target.selectionStart;

          // swap the character in front of the cursor if it's a supported character.
          // This would be soooo much easier in C...
          if (event.target.selectionStart > 0) {
            let char: string = event.target.value.charAt(idx - 1, 1);
            if ((char >= '0') && (char <= '9')) {
              let val = (parseInt(char) + diff);
              if (val > 9) val = 0;
              if (val < 0) val = 9;
              char = val.toString();
            }
            else if ((char >= 'a') && (char <= 'z')) {
              let val = String.fromCharCode(char.charCodeAt(0) + diff);
              if (val > 'z') val = 'a';
              if (val < 'a') val = 'z';
              char = val;
            }
            else if ((char >= 'A') && (char <= 'Z')) {
              let val = String.fromCharCode(char.charCodeAt(0) + diff);
              if (val > 'Z') val = 'A';
              if (val < 'A') val = 'Z';
              char = val;
            }

            event.target.value = event.target.value.substring(0, idx - 1)
              + char + event.target.value.substring(idx);
            event.target.selectionStart = idx;
            event.target.selectionEnd = idx;

            setTimeout(() => {
              event.target.dispatchEvent(new CustomEvent('input', { bubbles: true }))
            })
          }
          break;
        case FormatSpecifier.None:
        default:
          break;
      }
    }
  }

  // Input formatter for type-ahead inputs
  public formatInput(item: any): string {    
    let retVal = item; 
    if (typeof item !== 'string') {
      retVal = item.name;
    }
    return retVal; 
  }

  //  Scrolls the selected result of the typeahead into view if the typeahead window is open.
  public navigateResults(event) {

    if (!!(event.target.nextElementSibling)
      && ( event.target.nextElementSibling.localName == 'ngb-typeahead-window' )) {
      let activeButton: Element = event.target.nextElementSibling.querySelector('.active');

      if (activeButton)
        activeButton.scrollIntoView({ behavior: "smooth", block: "nearest"});
    }
  }
}

export class FormattedString {
  public displayValue: string; 
  public position: number;
  public actualValue: string | object; // has to be an object because it supports type aheads and not just string.
}

export class StringFormatter {
  public prefix: string;
  public suffix: string;

  constructor(prefix: string = '', suffix: string = '') {
    this.prefix = prefix;
    this.suffix = suffix;
  }

  public convert(input: string | object, caratPos: number = 0): FormattedString {
    let isString = (typeof input == 'string');
    // Return a default 
    var retVal = new FormattedString();
    // expecting type string but for type aheads can take in the object instead of a string.
    retVal.displayValue = isString ? input : input["name"]; 
    retVal.actualValue = input;
    retVal.position = caratPos; 

    return retVal;
  }

  // How may of the given significant characters are before the given position?
  protected getSignificantCharsBeforePosition(str: string, sigChars: RegExp, pos: number) {
    if (str == undefined)
      return 0;

    const segment = str.substring(0, pos);
    const replaced = segment.replace(sigChars, "");
    return segment.length - replaced.length;
  }

  // At what position in the input string have we skiped X of the significant characters? 
  protected getSignificantCharPosition(str: string, sigChars: RegExp, numberToSkip: number) {
    let hitCounter = 0;
    for (let i = 0; i < str.length; i++) {
      if (str[i].match(sigChars))
        ++hitCounter;
      else
        continue; 
      if (hitCounter >= numberToSkip)
        return i + 1;
    }
    return str.length;
  }
} 

export class CostCodeFormatter extends StringFormatter {

  constructor() {
    super("", "");
  }

  public convert(input: string, caratPos: number = 0): FormattedString {
    let retVal = new FormattedString();

    if (input == undefined)
      return retVal;
     
    input = input.toUpperCase(); 

    // Get the carat position within just the significant characters 
    let pos = this.getSignificantCharsBeforePosition(input, /[A-Z0-9]/g, caratPos);

    // Clean the value by removing all insignificant characters
    let cleaned = input.replace(/[^A-Z0-9]+/g, '');
    retVal.actualValue = cleaned.substr(0, Math.min(10, cleaned.length));

    // Mask up to ten significant characters with with undeerscores and hyphens 
    retVal.displayValue = retVal.actualValue.padEnd(10, "_");
    retVal.displayValue = retVal.displayValue.substr(0, 2)
      + "-" + retVal.displayValue.substr(2, 2)
      + "-" + retVal.displayValue.substr(4, 2)
      + "-" + retVal.displayValue.substr(6, 4); 

    // Figure out the new carat position 
    retVal.position = this.getSignificantCharPosition(retVal.displayValue, /[A-Z0-9]/g, pos);

    return retVal;
  }
}

export class NumberFormatter extends StringFormatter {
  public thouSep: boolean;
  public precision: number; 

  constructor(prefix: string = '', suffix: string = '', thouSep: boolean = true, precision: number = 0) {
    super(prefix, suffix);
    this.thouSep = thouSep; 
    this.precision = precision;
  }

  public convert(input: string, caratPos: number = 0): FormattedString {
    let retVal = new FormattedString();
    
    if (input == undefined)
      return retVal;

    // Sometimes we use numbers in text inputs and js treats them as numbers (e.g.Budget Manager)
    // so lets call toString() to make sure we definitely have a string and can
    // use all the string methods like trim, substring etc...
    input = input.toString();
    // Find out if it's negative. We handle this as its own separate token 
    const isNegative = input.trim().startsWith('-');

    // Get the carat position within just the significant characters. Don't include the negative sign in this
    let pos = this.getSignificantCharsBeforePosition(input, /[.0-9]/g, caratPos);

    // Clean the value and parse as a float, defaulting to zero if not a number.
    var cleanedInput = input.replace(/[^.0-9]+/g, ''); 
    var val = parseFloat(cleanedInput);
    if (val > 9999999999999) val = 9999999999999;  // limite to +/- 10^13-1 -- allowing numbers over this amoutn causes rounding error and float overruns 
    if (val < -9999999999999) val = -9999999999999; 
    if (isNaN(val))
      val = 0.0;

    // Split the value into dollar and cent components, rounding to two places
    var sign = (isNegative) ? '-' : ''; 
    var valSplit = val.toFixed(this.precision).split('.');
    var whole = valSplit[0];
    var decimal = (valSplit.length > 1) ? valSplit[1].padStart(2, "0") : "00";

    // Build the new display value by adding the currency and punctuation
    retVal.displayValue = sign + this.prefix
    retVal.displayValue  += (this.thouSep) ? whole.replace(/(\d)(?=(\d{3})+$)/g, '$1,') : whole;
    if (this.precision > 0)
      retVal.displayValue  += '.' + decimal;
    retVal.displayValue  += this.suffix; 

    // Set the actual value to just the rounded number
    if (isNegative)
      val *= -1; 
    retVal.actualValue = val.toFixed(this.precision);

    // Figure out the new carat position 
    retVal.position = this.getSignificantCharPosition(retVal.displayValue, /[.0-9]/g, pos);

    return retVal;
  }
}

//All of the UI widgets should contain:


//	// Visible features
//	* object.isDirty - some value no longer matches what was loaded

//	// hidden features
//	* when anything is changed, validate() is automatically called and errors and warnings are udpated
//	* udpates
//		object.isPristine - any change has been made to the value of the fieldToActUpon,
//					even if that change is reversed
//		field.isDirty - yhe value in the field is diffeent from how it was originally
